import { useQuery } from '@tanstack/react-query';
import { Badge, Button, buttonClass, Card, EmptyState, PageTitle, TEXT_LINK } from '@rooming/ui';
import { formatDateRangeInZone, formatInZone } from '@rooming/web-core';
import { Link } from 'react-router';
import {
  backoffice,
  type DistributionNotice,
  type HotelMessageNotice,
  type Notice,
  type PlacementNotice,
} from '../api';
import { Loaded, useCommand } from '../common';
import { useSession } from '../session';

// In-app notifications (prompt10 §5, prompt11 §6): administrators, hotels and guides are told when the signatory
// confirms a distribution — group, hotel, dates, who and when, and a passenger/room summary (counts only); a hotel is
// also told when the administrator leaves it a message (owner, 2026-10-07). Called "Notificaciones" on screen (it was
// "Avisos"). The inbox is re-read every minute and whenever the window regains focus.

const NOTICES_KEY = ['notifications'] as const;

const useNotices = () =>
  useQuery({ queryKey: NOTICES_KEY, queryFn: backoffice.notifications, refetchInterval: 60_000 });

/** "Notificaciones (2)" in the header. */
export function NoticesLink() {
  const notices = useNotices();
  const unread = notices.data?.unread ?? 0;
  return (
    <Link
      to="/notificaciones"
      aria-label={unread === 0 ? 'Notificaciones' : `Notificaciones: ${String(unread)} sin leer`}
      className="inline-flex min-h-11 items-center gap-2 rounded-lg px-3 text-base font-medium text-ink hover:bg-brand-50"
    >
      Notificaciones
      {unread > 0 && <Badge tone="info">{unread}</Badge>}
    </Link>
  );
}

export function NoticesPage() {
  const notices = useNotices();
  const { busy, run } = useCommand();
  const { me } = useSession();
  const external = me?.kind === 'EXTERNAL';
  return (
    <>
      {/* A visible way back (owner, 2026-10-07). */}
      <div className="mb-3">
        <Link to="/" className={TEXT_LINK}>
          {external ? '← Volver a Mis estadías' : '← Volver al Dashboard'}
        </Link>
      </div>
      <PageTitle
        subtitle={
          external
            ? 'Confirmaciones de distribución y mensajes de Funtour sobre tus estadías.'
            : 'Confirmaciones de distribución de tus grupos y estadías.'
        }
      >
        Notificaciones
      </PageTitle>
      <Loaded query={notices}>
        {(inbox) =>
          inbox.items.length === 0 ? (
            <EmptyState title="No tenés notificaciones">
              Cuando un firmante confirme una distribución, aparece acá.
            </EmptyState>
          ) : (
            <ul className="flex flex-col gap-3">
              {inbox.items.map((notice) => (
                <li key={notice.id}>
                  <NoticeCard
                    notice={notice}
                    stayLink={external ? `/estadias/${notice.stayId}` : undefined}
                    busy={busy}
                    onRead={() =>
                      void run(() => backoffice.markNotificationRead(notice.id), 'Marcado como leído.', [NOTICES_KEY])
                    }
                  />
                </li>
              ))}
            </ul>
          )
        }
      </Loaded>
    </>
  );
}

function NoticeCard({
  notice,
  stayLink,
  busy,
  onRead,
}: {
  readonly notice: Notice;
  /** Where "Ver la estadía" goes (hotel and guide); none for the administrator. */
  readonly stayLink: string | undefined;
  readonly busy: boolean;
  readonly onRead: () => void;
}) {
  return (
    <Card className="flex flex-col gap-2">
      {notice.kind === 'HOTEL_MESSAGE' ? (
        <HotelMessageBody notice={notice} />
      ) : notice.kind === 'PLACEMENT_REQUESTED' ? (
        <PlacementBody notice={notice} />
      ) : (
        <ConfirmationBody notice={notice} showSchool={stayLink === undefined} />
      )}
      <div className="flex flex-wrap gap-2">
        {notice.kind === 'PLACEMENT_REQUESTED' && stayLink === undefined && (
          <Link to="/solicitudes" className={buttonClass('primary')}>
            Ver solicitudes
          </Link>
        )}
        {stayLink !== undefined && (
          <Link to={stayLink} className={buttonClass('primary')}>
            Ver la estadía
          </Link>
        )}
        {!notice.read && (
          <Button variant="secondary" loading={busy} onClick={onRead}>
            Marcar como leído
          </Button>
        )}
      </div>
    </Card>
  );
}

/** A passenger with undefined sex asked for a bed: the administrator contacts the parents and decides. */
function PlacementBody({ notice }: { readonly notice: PlacementNotice }) {
  const { payload } = notice;
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold break-words">Solicitud de plaza · {payload.passenger}</h2>
        {!notice.read && <Badge tone="info">Nuevo</Badge>}
      </div>
      <p className="text-base break-words">
        Grupo {payload.group.code} · {payload.group.name} · {payload.hotel.name}
      </p>
      <p className="text-base break-words">
        Pidió una plaza en <strong>{payload.room}</strong> y su sexo figura «sin definir». Comunicate con la familia y
        resolvé la solicitud.
      </p>
    </>
  );
}

function HotelMessageBody({ notice }: { readonly notice: HotelMessageNotice }) {
  const { payload } = notice;
  const zone = payload.hotel.timezone;
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold break-words">Mensaje de Funtour · {payload.group.name}</h2>
        {!notice.read && <Badge tone="info">Nuevo</Badge>}
      </div>
      <p className="text-base break-words">
        {payload.hotel.name} · {formatDateRangeInZone(payload.stay.checkInAt, payload.stay.checkOutAt, zone)}
      </p>
      <p className="rounded-lg border-l-4 border-brand-500 bg-canvas p-3 text-base break-words whitespace-pre-line">
        {payload.message}
      </p>
      <p className="text-sm text-muted">Enviado el {formatInZone(payload.savedAt, zone)}.</p>
    </>
  );
}

/** Hotel and guide work with group, stay and hotel only: the school is an administrator's reference (prompt20 §1). */
function ConfirmationBody({
  notice,
  showSchool,
}: {
  readonly notice: DistributionNotice;
  readonly showSchool: boolean;
}) {
  const { payload } = notice;
  const zone = payload.hotel.timezone;
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold break-words">Distribución confirmada · {payload.group.name}</h2>
        {!notice.read && <Badge tone="info">Nuevo</Badge>}
      </div>
      <p className="text-base break-words">
        Grupo {payload.group.code}
        {showSchool ? ` · ${payload.school}` : ''}
      </p>
      <p className="text-base break-words">
        {payload.hotel.name} ({payload.hotel.city}) ·{' '}
        {formatDateRangeInZone(payload.stay.checkInAt, payload.stay.checkOutAt, zone)}
      </p>
      <p className="text-base">
        Confirmó {payload.confirmedBy} el {formatInZone(payload.confirmedAt, zone)} · {payload.status.assigned} de{' '}
        {payload.status.eligible} pasajeros con plaza
        {payload.status.unassigned > 0 ? ` (${String(payload.status.unassigned)} sin plaza)` : ''}.
      </p>
      {payload.rooms !== undefined && (
        <p className="text-base">
          {payload.rooms.inUse} de {payload.rooms.total} habitaciones en uso · {payload.rooms.freePlaces} plazas libres.
        </p>
      )}
    </>
  );
}
